import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { PayrollRunStatus } from "@wingspanhq/wingspan-api-sdk";

/**
 * Payroll runs are US-only by construction — W-2 payroll files US federal/state
 * withholding via MasterTax, and contractor payables execution settles through
 * the same US funding rails — so every amount on these screens is USD. Named
 * here so the assumption is stated once instead of repeated as a literal at each
 * call site, and so multi-currency support has a single place to start.
 */
export const PAYROLL_CURRENCY = Currency.USD;

/** First year W-2 payroll data exists for; the switcher runs from here to today. */
export const PAYROLL_RUNS_FIRST_YEAR = 2024;

/**
 * Which v3 `PayrollRunStatus` values count as "active". No v3 field marks a run
 * active vs finished, so the Active payrolls tab splits by status: in-flight runs
 * are still being built/approved/paid. The History tab is deliberately *not* the
 * complement — per the design it lists every run of the year (any status) and
 * lets the status filter narrow it.
 */
export const PAYROLL_RUNS_ACTIVE_STATUSES: PayrollRunStatus[] = [
  PayrollRunStatus.DRAFT,
  PayrollRunStatus.PENDING_APPROVALS,
  PayrollRunStatus.FINALIZED,
  PayrollRunStatus.PROCESSING
];

/**
 * Statuses that leave a run *open* for its pay period: it is still collecting or
 * approving work logs, so anything approved for that period lands in it and a
 * second run would compete with it. This is what blocks an off-cycle run — and
 * only this: once a run is finalized, paid, cancelled or failed, paying the work
 * logs it left behind is exactly what an off-cycle run is for.
 *
 * Deliberately narrower than `PAYROLL_RUNS_ACTIVE_STATUSES`, which also counts
 * `Finalized`/`Processing` (funds in flight, but the run can no longer absorb a
 * work log).
 */
export const PAYROLL_RUNS_OPEN_STATUSES: PayrollRunStatus[] = [
  PayrollRunStatus.DRAFT,
  PayrollRunStatus.PENDING_APPROVALS
];

/**
 * The status choices offered by the payroll history filter, in display order.
 *
 * Several v3 statuses share one user-facing status on these screens (see
 * `selectorPayrollRunStatusPill`), so a choice maps to a *set* of
 * `PayrollRunStatus` values rather than a single one.
 */
export const PAYROLL_RUNS_STATUS_FILTER_OPTIONS: Array<{
  label: string;
  statuses: PayrollRunStatus[];
}> = [
  { label: "Opened", statuses: [PayrollRunStatus.DRAFT] },
  {
    label: "Pending",
    statuses: [
      PayrollRunStatus.PENDING_APPROVALS,
      PayrollRunStatus.PARTIALLY_PAID
    ]
  },
  {
    label: "Processing",
    statuses: [PayrollRunStatus.FINALIZED, PayrollRunStatus.PROCESSING]
  },
  { label: "Completed", statuses: [PayrollRunStatus.PAID] },
  { label: "Cancelled", statuses: [PayrollRunStatus.CANCELLED] },
  { label: "Failed", statuses: [PayrollRunStatus.FAILED] }
];

/** Ordered approval-window stages shown in the payroll run details stepper. */
export const PAYROLL_RUN_APPROVAL_STAGES = [
  "Open",
  "Approval",
  "Finalized",
  "Paid"
] as const;
